import {
  type Call,
  type CallKind,
  callsByRun,
  type Inline,
  type InlineRun,
  isHeading,
  type Lang,
  type Mark,
  markSchema,
  type NodeContent,
  nodeContentSchema,
  runsOf,
  withRunAt,
} from './content'

/**
 * A run as an editor holds it, and back again.
 *
 * The editor edits prose, so it needs the run as a flat sequence of nodes a
 * contenteditable can carry — and it has to hand back a run that differs from
 * the one it was given only where the author typed. That second half is the
 * whole difficulty: a call is an invisible atom with no text of its own, and a
 * conversion that loses one destroys a note's marker or a citation while leaving
 * prose that reads perfectly — and worse now, shifts every call after it onto
 * the wrong target, since a call is found by its position.
 *
 * So the pair is written here rather than in the editor, beside the schema it
 * is about, and the round trip is asserted over every node kind.
 *
 * **Language is a mark here and a field in the model**, which is the one real
 * mismatch. An editor node carries marks and nothing else, so `lang` travels as
 * one; `toRun` puts it back on the node where the model keeps it. Encoding it
 * as anything but a mark would leave the editor unable to set a Hebrew phrase
 * inside an English sentence, which this material does constantly.
 */

/** A mark as the editor carries it: a name, and attributes where it has any. */
export type EditorMark =
  | { type: Mark }
  | { type: 'lang'; attrs: { lang: Lang } }

/**
 * One node of a run, in the editor's shape.
 *
 * One atom where there were three, carrying no text. It DOES carry what the call
 * names, and that is not the id returning to the words: the stored JSON keeps
 * only `{t:'call',of}`, and this is the editor knowing which call it has hold
 * of, the same way the renderer does.
 *
 * It has to. A call is found by its POSITION, so deleting one of two note
 * markers changes what the other resolves to — and with only the kinds in hand
 * there is no way to say WHICH note went. Carrying the target makes deletion and
 * reordering ordinary edits that take their calls with them; without it the save
 * can only be refused.
 *
 * An editor still cannot AUTHOR one: a marker typed into prose has nothing to
 * name, and creating a note or a quotation is its own act, which writes the
 * marker and its row together.
 */
export type EditorNode =
  | { type: 'text'; text: string; marks?: EditorMark[] }
  | { type: 'call'; attrs: { of: CallKind; id: string } }
  | { type: 'hardBreak' }

/*
 * Marks are compared as a set, so they are ordered before they are compared.
 *
 * An editor sorts its own marks, so two runs that differ only in the order
 * their marks were written come back identical — and a comparison that read
 * the order would call that an edit and refuse a write nobody made.
 */
const MARK_ORDER = markSchema.options
const byMarkOrder = (a: Mark, b: Mark) =>
  MARK_ORDER.indexOf(a) - MARK_ORDER.indexOf(b)

const sameMarks = (a: Mark[] | undefined, b: Mark[] | undefined) => {
  const left = a ?? []
  const right = b ?? []
  return left.length === right.length && left.every((m, i) => m === right[i])
}

/**
 * The one form of a run that survives the editor unchanged.
 *
 * Three things an editor does to a run whatever anybody intends: it drops empty
 * text, it orders marks, and it merges neighboring text that carries the same
 * ones. All three are meaning-preserving and none is reversible, so a run is
 * put into that form BEFORE it is compared with what came back — otherwise a
 * book opened and closed with nothing typed reads as edited.
 *
 * It is deliberately not applied on the way to the database by anything but the
 * editor: an imported run that splits a sentence into three identical spans is
 * odd rather than wrong, and rewriting the corpus is not this function's job.
 */
export function normalizeRun(run: InlineRun): InlineRun {
  const out: InlineRun = []

  for (const node of run) {
    if (node.t !== 'text') {
      out.push(node)
      continue
    }
    // Empty text is not a node an editor can hold, so it is not one a
    // comparison may expect back.
    if (node.v === '') continue

    const marks = node.marks?.length
      ? [...node.marks].sort(byMarkOrder)
      : undefined

    const previous = out.at(-1)
    if (
      previous?.t === 'text' &&
      previous.lang === node.lang &&
      sameMarks(previous.marks, marks)
    ) {
      previous.v += node.v
      continue
    }

    out.push({
      t: 'text',
      v: node.v,
      ...(node.lang ? { lang: node.lang } : {}),
      ...(marks ? { marks } : {}),
    })
  }

  return out
}

/** A run in the shape an editor can hold. */
export function toEditor(run: InlineRun, calls: Call[] = []): EditorNode[] {
  /* The calls this run makes, taken in turn — the words say a call sits here and
     the rows say which, so the two are matched by position exactly as every
     reader matches them. */
  let at = 0
  return normalizeRun(run).map((node): EditorNode => {
    switch (node.t) {
      case 'text': {
        const marks: EditorMark[] = [
          ...(node.marks ?? []).map((type) => ({ type })),
          ...(node.lang
            ? [{ type: 'lang' as const, attrs: { lang: node.lang } }]
            : []),
        ]
        return marks.length
          ? { type: 'text', text: node.v, marks }
          : { type: 'text', text: node.v }
      }
      case 'call': {
        const called = calls[at++]
        return {
          type: 'call',
          attrs: { of: node.of, id: called?.id ?? '' },
        }
      }
      case 'break':
        return { type: 'hardBreak' }
      default: {
        /* Every inline kind is handled above, and this is what keeps that true:
           a kind added to the schema is a type error here rather than a node
           that quietly does not survive the round trip. */
        const unhandled: never = node
        throw new Error(`Unhandled inline node: ${JSON.stringify(unhandled)}`)
      }
    }
  })
}

/**
 * What the editor holds, back as a run.
 *
 * Normalized on the way out as well as in, because the editor is not the only
 * thing that can hand this a sequence — and a run written to the database in
 * two forms that mean the same thing is how a passage comes to look edited
 * every time it is opened.
 */
export function fromEditor(nodes: EditorNode[]): {
  run: InlineRun
  calls: Call[]
} {
  /* What survived the edit, in the order it survived in. A deleted atom takes
     its call; a moved one carries it. */
  const calls: Call[] = []
  const run: InlineRun = nodes.map((node): Inline => {
    switch (node.type) {
      case 'text': {
        const marks = (node.marks ?? [])
          .filter((m): m is { type: Mark } => m.type !== 'lang')
          .map((m) => m.type)
        const lang = (node.marks ?? []).find(
          (m): m is { type: 'lang'; attrs: { lang: Lang } } =>
            m.type === 'lang',
        )?.attrs.lang
        return {
          t: 'text',
          v: node.text,
          ...(lang ? { lang } : {}),
          ...(marks.length ? { marks: marks.sort(byMarkOrder) } : {}),
        }
      }
      case 'call':
        calls.push({ of: node.attrs.of, id: node.attrs.id })
        return { t: 'call', of: node.attrs.of }
      case 'hardBreak':
        return { t: 'break' }
      default: {
        /* The same guard from the other side: an editor node this does not know
           is a type error rather than a run that comes back short. */
        const unhandled: never = node
        throw new Error(`Unhandled editor node: ${JSON.stringify(unhandled)}`)
      }
    }
  })

  return { run: normalizeRun(run), calls }
}

/** One run of a block, named so an editor can show it and write it back. */
export type EditableRun = {
  /** Where it sits inside the content — `he`, `items.2`, `turns.1.speaker`. */
  path: string
  /** What this run IS within the block, in a person's words. */
  label: string
  run: InlineRun
}

/**
 * Every run of a block that holds words somebody wrote.
 *
 * The one place that says which fields of which kind are text. A screen asking
 * the block itself is a screen that grows a `switch` per surface, and the day a
 * kind gains a field the surfaces disagree about whether it can be edited —
 * which is the same failure the content union exists to prevent for the
 * renderer.
 *
 * A `divider` and a `figure`'s asset yield nothing: neither holds words, so
 * neither is missing from here.
 */
export function editableRuns(content: {
  kind: string
  [key: string]: unknown
}): EditableRun[] {
  /* The same list `runsOf` states, because they are the same question asked by
     an editor and by a renderer. Held apart, they drifted: a quotation's
     attribution was offered as editable and was not counted as a run, so a
     citation written into a source line printed on the page while nothing
     asking what the block referenced could see it.

     Which is now more than untidy — a call is found by its POSITION among the
     runs, so two lists that disagree hand every marker after the disagreement
     the wrong target.

     A heading answers empty here: its title is written through the part rather
     than through a passage editor, and offering it would be a second way in. */
  const said = content as unknown as NodeContent
  return isHeading(said) ? [] : runsOf(said)
}

/**
 * One run of a passage written, and the calls that travel with it.
 *
 * A passage's calls are ONE sequence across its runs, so writing a run replaces
 * that run's share and leaves every other run holding what it held — which is
 * what keeps a marker somewhere else in the passage pointing at what it always
 * did.
 *
 * Each share is keyed by its run's PATH and read back in the order the WRITTEN
 * content states, because a run can APPEAR: naming the speaker of a turn that
 * resumes adds one, and a position that means one run before the write and its
 * neighbor after it hands every marker past that point the wrong row.
 */
export function writeRun<T extends { kind: string }>(
  content: T,
  calls: Call[],
  path: string,
  run: InlineRun,
  runCalls: Call[],
): { content: T; calls: Call[] } {
  const written = withRunAt(content, path, run)
  /* Cast for the reason `editableRuns` casts: these are structural walks over
     the fields a kind declares, and a draft claims a kind. */
  const said = content as unknown as NodeContent
  const per = callsByRun(said, calls)
  const held = new Map(runsOf(said).map((r, i) => [r.path, per[i] ?? []]))
  held.set(path, runCalls)
  return {
    content: written,
    calls: runsOf(written as unknown as NodeContent).flatMap(
      (r) => held.get(r.path) ?? [],
    ),
  }
}

/**
 * The draft as the book will hold it.
 *
 * **A run left empty where the model refuses an empty one is stored as the
 * field being ABSENT.** A turn's speaker is the field that is: a turn names a
 * speaker or names none, so clearing the name is the author saying this turn
 * resumes the speech before it, and storing a run of no words there states the
 * one thing the model rejects. Refusing the ordinary gesture at the save leaves
 * the author holding a passage the page gives them no way to make legal.
 *
 * Which fields those are is ASKED of the schema rather than listed here — a
 * list would be a second statement of what is optional, drifting from the one
 * the schema makes. Content the model already admits comes back untouched, so
 * a cleared translation or attribution stays a field standing empty, still
 * offered and still fillable.
 *
 * Every such run is settled at once, because what makes a passage legal is the
 * state it ends in. A dialogue whose second resumed turn was also typed into
 * and cleared has two names to drop, and dropping one of them leaves a passage
 * the save still refuses — over a field the author deliberately emptied, and
 * taking every unrelated edit in the passage down with it.
 *
 * The settling is taken only where it leaves the whole passage legal. A draft
 * still wrong for some other reason is returned as it stands, so the save says
 * what is actually wrong with it rather than reporting a field this quietly
 * removed.
 */
export function asStored<T extends { kind: string }>(content: T): T {
  if (nodeContentSchema.safeParse(content).success) return content
  /* Cast for the same reason `editableRuns` casts: this is a structural walk
     over the fields a kind declares, and a draft claims a kind. */
  const settled = runsOf(content as unknown as NodeContent).reduce(
    (draft, { run, path }) =>
      run.length ? draft : withRunAt(draft, path, run, false),
    content,
  )
  return nodeContentSchema.safeParse(settled).success ? settled : content
}

/**
 * Every call a run makes, in reading order, as KINDS.
 *
 * What an edit is checked against: the sequence before and after must match, or
 * the edit took a marker or a citation off the page.
 *
 * Kinds rather than ids, because the ids are leaving the words — what a marker
 * NAMES is a row, and a placeholder in the text says only that a call sits here.
 * The guarantee is unchanged by that: which call a marker is, is its position
 * among them, so a run that still makes the same calls in the same order still
 * resolves every one of them to the same row. An edit that dropped the second of
 * three shifts the third, and this is what sees it.
 */
export function callKindsIn(run: InlineRun): CallKind[] {
  return run.flatMap((node) => (node.t === 'call' ? [node.of] : []))
}
